import { useEffect, useState } from 'react';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { ReadyToSharePostWithPlatform } from 'src/app/shared/api/readyToShare';

import type { UseFetchReadyToSharePostsByPlatformReturn } from '../useFetchReadyToSharePostsByPlatform';
import { useFetchReadyToSharePostsByPlatform } from '../useFetchReadyToSharePostsByPlatform';

// Platforms to fetch ready-to-share posts from. Currently includes Facebook, Instagram, Reddit, and Facebook Groups.
// Update this list if additional platforms are supported in the future.
const PLATFORMS_TO_FETCH = ['FACEBOOK', 'INSTAGRAM', 'REDDIT', 'FACEBOOKGROUPS'] as const;

// Platform priority order for displaying posts. If the first platform has no data or fails,
// the next platform in the list will be used.
const PLATFORM_PRIORITY = ['FACEBOOK', 'INSTAGRAM', 'REDDIT', 'FACEBOOKGROUPS'] as const;

/**
 * Custom hook that handles the effect logic for platform data changes
 */
function usePlatformDataEffect(
	platformDataResult: UseFetchReadyToSharePostsByPlatformReturn,
	setLoading: (loading: boolean) => void,
	setShouldShowReadyToShareExperiment: (show: boolean) => void,
	setReadyToSharePostsValue: (posts: readonly ReadyToSharePostWithPlatform[] | undefined) => void,
) {
	const { platformData, hasAnyData, allComplete, allFailed, anyPending, allDone } = platformDataResult;

	useEffect(() => {
		/**
		 * Stop if all platforms failed
		 */
		if (allFailed) {
			setLoading(false);
			setShouldShowReadyToShareExperiment(false);
			return;
		}

		/**
		 * Progressive loading: Show data as soon as available
		 * Stop showing loading UI immediately when first platform has data
		 * (Polling continues in background until all platforms are done)
		 */

		// Find first platform with complete data based on priority order
		// This ensures we show data even if the preferred platform fails
		const firstAvailablePlatform = PLATFORM_PRIORITY.find((platform) => {
			const data = platformData[platform];
			return data?.status === 'COMPLETE' && data.items.length > 0;
		});

		// Show posts from the first available platform
		if (firstAvailablePlatform && platformData[firstAvailablePlatform]) {
			setReadyToSharePostsValue(platformData[firstAvailablePlatform].items);
		}

		// Stop loading UI as soon as we have ANY data
		if (hasAnyData) {
			setLoading(false);
		}

		// If all platforms are done and still no data, hide the experiment
		if (allDone && !hasAnyData) {
			setLoading(false);
			setShouldShowReadyToShareExperiment(false);
		}
	}, [
		hasAnyData,
		allComplete,
		allFailed,
		anyPending,
		allDone,
		platformData,
		setLoading,
		setReadyToSharePostsValue,
		setShouldShowReadyToShareExperiment,
	]);
}

export function useHandlePolling() {
	const {
		data: {
			petition: { slug },
			viewer: {
				experiments: { starterExperienceDashboardReadyToShareV2 },
			},
		},
	} = useStarterDashboardV2PageData();
	const isVariation = starterExperienceDashboardReadyToShareV2.variation === 'variant1';
	/**
	 * Initialize module visibility based on experiment variation only.
	 * The module will start polling for data immediately and hide itself if no data is found after all platforms complete.
	 * This ensures the module appears for new petitions without preview links yet.
	 */
	const [shouldShowReadyToShareExperiment, setShouldShowReadyToShareExperiment] = useState<boolean>(isVariation);
	const [isLoading, setLoading] = useState<boolean>(isVariation);
	const [readyToSharePostsValue, setReadyToSharePostsValue] = useState<
		readonly ReadyToSharePostWithPlatform[] | undefined
	>(undefined);

	const platformDataResult = useFetchReadyToSharePostsByPlatform({
		slug,
		platforms: PLATFORMS_TO_FETCH,
		shouldExecute: shouldShowReadyToShareExperiment,
	});

	// Handle platform data changes with progressive loading
	usePlatformDataEffect(platformDataResult, setLoading, setShouldShowReadyToShareExperiment, setReadyToSharePostsValue);

	return {
		isLoading,
		readyToSharePostsValue,
		shouldShowReadyToShareExperiment,
		platformData: platformDataResult.platformData, // Expose for progressive loading UI
	};
}
